import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { runContentStrategy } from "src/common/Scraper";
import { KeywordIntelligenceRunPayload } from "../../types/keyword-assist.type";
import { sleep } from "src/common/config/FunctionHelper";
import useUpdateUserAgents from "../../hooks/useUpdateUserAgents";
import { posthogService, PostHogEvent } from "src/common/services/posthogService";

interface UseContentStrategyMutationProps {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onMutateStart?: () => void;
}

export function useContentStrategyMutation({
  onSuccess,
  onError,
  onMutateStart
}: UseContentStrategyMutationProps = {}) {
  const queryClient = useQueryClient();
  const { mutate: updateAgents } = useUpdateUserAgents('content-strategy');

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [keywords, setKeywords] = useState<string[]>([]);

  const runContentStrategyMutation = useMutation({
    mutationFn: (payload: KeywordIntelligenceRunPayload) =>
      runContentStrategy(payload),
    onMutate: (variables) => {
      if (variables?.site_id) {
        queryClient.setQueryData(['agentsStatus', variables.site_id], (oldData: any) => ({
          ...oldData,
          agents: {
            ...oldData?.agents,
            'content-strategy': {
              ...oldData?.agents?.['content-strategy'],
              status: 'processing',
            },
          },
        }));
      }
    },
    onSuccess: (data, variables) => {
      // Track Orbit AI Content Strategy generation in PostHog
      // Captures site ID, user ID, and count of competitor keywords analyzed
      posthogService.track(PostHogEvent.ORBIT_AI_CONTENT_STRATEGY_CREATED, {
        site_id: variables?.site_id,
        user_id: variables?.user_id,
        competitor_keywords_count: variables?.competitor_keywords?.length || 0
      });
      queryClient.invalidateQueries({
        queryKey: ["content-strategy", variables?.user_id, variables?.site_id],
      });
      if (variables?.site_id) {
        queryClient.setQueryData(['agentsStatus', variables.site_id], (oldData: any) => ({
          ...oldData,
          agents: {
            ...oldData?.agents,
            'content-strategy': {
              ...oldData?.agents?.['content-strategy'],
              status: 'processing',
            },
          },
        }));
        queryClient.invalidateQueries({
          queryKey: ["agentsStatus", variables.site_id],
        });
      }
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error, variables) => {
      if (variables?.site_id) {
        queryClient.invalidateQueries({
          queryKey: ["agentsStatus", variables.site_id],
        });
      }
      if (onError) {
        onError(error);
      }

      handleAgentStatusUpdate(false, variables?.site_id, variables?.competitor_keywords, 'content_strategy_dashboard');
    },
  });

  function handleAgentStatusUpdate(status: boolean, overrideSiteId?: string, competitorKeywords?: string[], source: string = 'content_strategy_dashboard') {
    updateAgents({
      site_id: overrideSiteId,
      source,
      agents: [
        {
          name: 'content_strategy',
          enabled: status,
          config: {
            competitors: competitorKeywords || keywords
          }
        }
      ]
    });
  }

  const handleEnableContentStrategy = async (kws: string[], overrides: { siteId: string; userId: string, shouldRun?: boolean, source?: string }) => {
    const effectiveSiteId = overrides?.siteId;
    const effectiveUserId = overrides?.userId;
    const shouldRun = overrides?.shouldRun ?? true;
    const source = overrides?.source || 'content_strategy_dashboard';

    if (onMutateStart) {
      onMutateStart();
    }
    setDialogOpen(false);

    handleAgentStatusUpdate(true, overrides?.siteId, kws, source);

    if (shouldRun) {
      runContentStrategyMutation.mutate({
        stream: false,
        site_id: effectiveSiteId,
        user_id: effectiveUserId,
        competitor_keywords: kws,
      });
    }
  };

  return {
    dialogOpen,
    setDialogOpen,
    keywords,
    setKeywords,
    handleEnableContentStrategy,
    runContentStrategyMutation,
  };
}
